import { useMemo, useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { BarChart3, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "../../lib/utils";

export interface GapData {
  id: string | number;
  gap: number;
  timestamp?: Date;
  tierId?: number | null;
  tierName?: string | null;
  tierColor?: string | null;
}

interface StreakAnalysisChartProps {
  gaps: GapData[];
  currentGap: number;
  averageGap: number;
  tierName?: string;
  tierColor?: string;
  maxDisplayed?: number;
  isLoading?: boolean;
  multiTier?: boolean;
  compact?: boolean;
  totalWins?: number;
  observedProbability?: number;
  /** Min gap across ALL data in the timeframe (not just displayed bars) */
  overallMinGap?: number | null;
  /** Max gap across ALL data in the timeframe (not just displayed bars) */
  overallMaxGap?: number | null;
}

export function StreakAnalysisChart({
  gaps,
  currentGap,
  averageGap,
  tierName = "Tier Wins",
  tierColor,
  maxDisplayed = 20,
  isLoading,
  multiTier = false,
  compact = false,
  totalWins,
  observedProbability,
  overallMinGap,
  overallMaxGap,
}: StreakAnalysisChartProps) {
  const displayedGaps = gaps.slice(-maxDisplayed);
  const [selectedBarIndex, setSelectedBarIndex] = useState<number | "current" | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside the chart
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (chartRef.current && !chartRef.current.contains(event.target as Node)) {
        setSelectedBarIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const allGaps = displayedGaps.map((g) => g.gap);
    if (allGaps.length === 0) {
      return { min: 0, max: 0, chartMax: Math.max(currentGap, averageGap, 10), currentStreak: 0, bestStreak: 0 };
    }
    const min = Math.min(...allGaps);
    const max = Math.max(...allGaps);
    // Chart max should accommodate all values plus some headroom
    const chartMax = Math.max(max, currentGap, averageGap, 10) * 1.1;

    // Calculate current streak (consecutive 0-gaps at the end)
    // A streak of N means N consecutive wins with gap=0 between them
    let currentStreak = 1; // Start with the most recent win
    for (let i = allGaps.length - 1; i >= 0; i--) {
      if (allGaps[i] === 0) {
        currentStreak++;
      } else {
        break;
      }
    }
    // If currentGap > 0, the streak is "ended" (we've had spins since last win)
    if (currentGap > 0) {
      currentStreak = 0;
    }

    // Calculate best streak in the data
    let bestStreak = 1;
    let tempStreak = 1;
    for (const gap of allGaps) {
      if (gap === 0) {
        tempStreak++;
        bestStreak = Math.max(bestStreak, tempStreak);
      } else {
        tempStreak = 1;
      }
    }

    return { min, max, chartMax, currentStreak, bestStreak };
  }, [displayedGaps, currentGap, averageGap]);

  // Determine variance status
  // Higher gap = longer since last win = more "overdue" = better odds (gambler's fallacy aside)
  const getVarianceStatus = () => {
    if (displayedGaps.length === 0) {
      return { label: "Collecting data", color: "secondary" as const, icon: null };
    }
    if (averageGap === 0) {
      return { label: "Normal", color: "secondary" as const, icon: null };
    }
    const ratio = currentGap / averageGap;
    if (ratio > 2) {
      // Way above average - significantly overdue
      return { label: "Overdue", color: "warning" as const, icon: TrendingUp };
    }
    if (ratio > 1) {
      // Above average - due soon
      return { label: "Above avg", color: "default" as const, icon: TrendingUp };
    }
    if (ratio < 0.5) {
      // Well below average - just won recently
      return { label: "Recent win", color: "success" as const, icon: TrendingDown };
    }
    return { label: "Normal", color: "secondary" as const, icon: null };
  };

  const variance = getVarianceStatus();

  if (isLoading) {
    return (
      <Card>
        <CardHeader className={cn(compact ? "pb-1 pt-3" : "")}>
          <CardTitle className={cn(compact ? "text-base" : "text-lg")}>
            {compact ? tierName : "Gap Analysis"}
          </CardTitle>
        </CardHeader>
        <CardContent className={cn(compact && "pb-3 pt-2")}>
          <div className={cn("animate-pulse bg-secondary rounded-lg", compact ? "h-24" : "h-48")} />
        </CardContent>
      </Card>
    );
  }

  // Empty state - no gaps and no current gap
  if (displayedGaps.length === 0 && currentGap === 0) {
    return (
      <Card>
        <CardHeader className={cn(compact ? "pb-1 pt-3" : "pb-2")}>
          <CardTitle className={cn(compact ? "text-base" : "text-lg")}>
            {compact ? tierName : `Gap Analysis — ${tierName}`}
          </CardTitle>
        </CardHeader>
        <CardContent className={cn(compact && "pb-3 pt-2")}>
          <div className={cn("flex flex-col items-center justify-center text-center", compact ? "h-24 py-4" : "h-48")}>
            <BarChart3 className={cn("text-muted-foreground/50", compact ? "w-8 h-8 mb-2" : "w-12 h-12 mb-3")} />
            <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>
              {compact ? "No data yet" : "No gap data available yet"}
            </p>
            {!compact && (
              <p className="text-sm text-muted-foreground/70 mt-1">
                Gaps will appear as tier wins are recorded
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate bar height with minimum visibility
  const getBarHeight = (gap: number) => {
    const percentage = (gap / stats.chartMax) * 100;
    // Minimum 2% height so bars are always visible
    return Math.max(percentage, 2);
  };

  // Get bar color based on whether above/below average
  // Current gap uses same logic but with higher opacity
  const getBarColor = (gap: number) => {
    const isAboveAverage = gap > averageGap;
    if (tierColor) {
      // Use opacity variations of tier color
      return isAboveAverage ? "bg-orange-500/90" : "";
    }
    return isAboveAverage ? "bg-orange-500/80" : "bg-primary/80";
  };

  // Current gap is above/below average
  const isCurrentAboveAverage = currentGap > averageGap;

  return (
    <Card>
      <CardHeader className={cn(compact ? "pb-1 pt-3" : "pb-2")}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className={cn(compact ? "text-base" : "text-lg")}>
            {compact ? tierName : `Gap Analysis — ${tierName}`}
          </CardTitle>
          {!compact && (
            <div className="flex items-center gap-2">
              {(overallMinGap ?? stats.min) > 0 && (
                <span className="text-xs text-muted-foreground">
                  Min: <span className="font-medium text-foreground">{overallMinGap ?? stats.min}</span>
                </span>
              )}
              {(overallMaxGap ?? stats.max) > 0 && (
                <span className="text-xs text-muted-foreground">
                  Max: <span className="font-medium text-foreground">{overallMaxGap ?? stats.max}</span>
                </span>
              )}
            </div>
          )}
        </div>
        <div className={cn("flex items-center flex-wrap", compact ? "mt-1 gap-2 justify-between" : "mt-2 gap-4")}>
          <div className="flex items-center gap-3 flex-wrap">
            {!compact && (
              <Badge variant={variance.color} className="flex items-center gap-1">
                {variance.icon && <variance.icon className="w-3 h-3" />}
                {variance.label}
              </Badge>
            )}
            <span className={cn(compact ? "text-xs" : "text-sm", "text-muted-foreground")}>
              Current: <span className={cn("font-semibold", isCurrentAboveAverage ? "text-orange-400" : "text-primary")}>{currentGap}</span>
            </span>
            {averageGap > 0 && (
              <span className={cn(compact ? "text-xs" : "text-sm", "text-muted-foreground")}>
                Avg: <span className="text-foreground font-medium">{Math.round(averageGap)}</span>
              </span>
            )}
            {compact && (overallMaxGap ?? stats.max) > 0 && (
              <span className="text-xs text-muted-foreground">
                Max: <span className="text-foreground font-medium">{overallMaxGap ?? stats.max}</span>
              </span>
            )}
            {!compact && (
              <>
                <span className="text-sm text-muted-foreground">
                  Streak: <span className={cn("font-semibold", stats.currentStreak >= 2 ? "text-emerald-400" : "text-foreground")}>{stats.currentStreak}x</span>
                </span>
                {stats.bestStreak >= 2 && stats.currentStreak < stats.bestStreak && (
                  <span className="text-sm text-muted-foreground">
                    Best: <span className="text-foreground font-medium">{stats.bestStreak}x</span>
                  </span>
                )}
              </>
            )}
          </div>
          {compact && totalWins !== undefined && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">
                {totalWins} {totalWins === 1 ? 'win' : 'wins'}
              </span>
              {observedProbability !== undefined && observedProbability > 0 && (
                <span className="text-xs text-muted-foreground">
                  • <span className="font-medium text-foreground">{(observedProbability * 100).toFixed(2)}%</span> rate
                </span>
              )}
              {variance.label !== "Collecting data" && (
                <Badge variant={variance.color} className="text-[10px] px-1.5 py-0 h-4">
                  {variance.label}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className={cn(compact && "pb-3 pt-2")} ref={chartRef}>
        {/* Y-axis + Bars container */}
        <div className="flex">
          {/* Y-axis labels */}
          <div className={cn("flex flex-col justify-between pr-2 text-xs text-muted-foreground w-10 shrink-0", compact ? "h-24 text-[10px] w-8" : "h-48")}>
            <span>{Math.round(stats.chartMax)}</span>
            <span>{Math.round(stats.chartMax / 2)}</span>
            <span>0</span>
          </div>

          {/* Bars only */}
          <div className={cn("flex-1 min-w-0 relative flex items-end gap-0.5", compact ? "h-24" : "h-48")}>
            {/* Average line */}
            {averageGap > 0 && (
              <>
                <div
                  className="absolute left-0 right-0 border-t-2 border-dashed border-yellow-500/50 z-10"
                  style={{ bottom: `${(averageGap / stats.chartMax) * 100}%` }}
                />
                <div
                  className="absolute right-0 text-[10px] text-yellow-500 bg-card px-1 rounded z-10"
                  style={{ bottom: `${(averageGap / stats.chartMax) * 100}%`, transform: "translateY(50%)" }}
                >
                  avg
                </div>
              </>
            )}

            {/* Gap bars */}
            {displayedGaps.map((gapData, index) => {
              const height = getBarHeight(gapData.gap);
              const isAboveAverage = gapData.gap > averageGap;
              const barColor = getBarColor(gapData.gap);
              const isSelected = selectedBarIndex === index;

              return (
                <div
                  key={gapData.id}
                  className="flex-1 h-full flex flex-col justify-end group relative min-w-1"
                  onClick={() => setSelectedBarIndex(isSelected ? null : index)}
                >
                  <div
                    className={cn(
                      "w-full rounded-t transition-all hover:opacity-100 cursor-pointer",
                      barColor,
                      isAboveAverage ? "opacity-90" : "opacity-70",
                      isSelected && "ring-2 ring-white/50"
                    )}
                    style={{
                      height: `${height}%`,
                      backgroundColor: (multiTier && gapData.tierColor) || (tierColor && !isAboveAverage)
                        ? (multiTier ? (gapData.tierColor ?? undefined) : tierColor)
                        : undefined,
                    }}
                  />
                  {/* Tooltip - show on hover (desktop) or when selected (mobile) */}
                  <div className={cn(
                    "absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-20 pointer-events-none",
                    isSelected ? "block" : "hidden group-hover:block"
                  )}>
                    <div className="bg-popover text-popover-foreground text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap border border-border">
                      <div className="font-medium">Gap: {gapData.gap}</div>
                      {multiTier && gapData.tierName && (
                        <div className="text-xs" style={{ color: gapData.tierColor || undefined }}>
                          {gapData.tierName}
                        </div>
                      )}
                      <div className="text-muted-foreground">
                        #{displayedGaps.length - index} win ago
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Current gap indicator */}
            <div
              className="flex-1 h-full flex flex-col justify-end group relative min-w-1.5"
              onClick={() => setSelectedBarIndex(selectedBarIndex === "current" ? null : "current")}
            >
              <div
                className={cn(
                  "w-full rounded-t transition-all border-2 border-white/30 cursor-pointer",
                  getBarColor(currentGap),
                  isCurrentAboveAverage ? "opacity-100" : "opacity-90",
                  currentGap > 0 && "animate-pulse",
                  selectedBarIndex === "current" && "ring-2 ring-white/50"
                )}
                style={{
                  height: `${getBarHeight(currentGap)}%`,
                  backgroundColor: tierColor && !isCurrentAboveAverage ? tierColor : undefined,
                }}
              />
              {/* Tooltip - show on hover (desktop) or when selected (mobile) */}
              <div className={cn(
                "absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-20 pointer-events-none",
                selectedBarIndex === "current" ? "block" : "hidden group-hover:block"
              )}>
                <div className="bg-popover text-popover-foreground text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap border border-border">
                  <div className={cn("font-medium", isCurrentAboveAverage ? "text-orange-400" : "text-primary")}>
                    Current: {currentGap}
                  </div>
                  <div className="text-muted-foreground">Pulls since last win</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Labels row - completely outside Y-axis flex, with margin to align with bars */}
        <div className={cn("flex gap-0.5 mt-1", compact ? "ml-8 h-7 sm:h-auto" : "ml-10 h-7 sm:h-auto")}>
          {displayedGaps.map((gapData) => {
            const isMinGap = gapData.gap === stats.min && stats.min > 0;
            const isMaxGap = gapData.gap === stats.max;
            return (
              <div key={gapData.id} className="flex-1 flex justify-center min-w-1">
                <span className={cn(
                  "font-medium text-muted-foreground [writing-mode:vertical-lr] sm:[writing-mode:horizontal-tb] rotate-180 sm:rotate-0 text-right sm:text-center text-[11px] sm:text-xs",
                  isMinGap && "text-emerald-400",
                  isMaxGap && "text-orange-400"
                )}>
                  {gapData.gap}
                </span>
              </div>
            );
          })}
          {/* Current gap label */}
          <div className="flex-1 flex justify-center min-w-1.5">
            <span className={cn(
              "font-bold [writing-mode:vertical-lr] sm:[writing-mode:horizontal-tb] rotate-180 sm:rotate-0 text-right sm:text-center text-[11px] sm:text-xs",
              isCurrentAboveAverage ? "text-orange-400" : "text-primary"
            )}>
              {currentGap}
            </span>
          </div>
        </div>

        {/* X-axis labels */}
        {!compact && (
          <div className="flex justify-between mt-1 ml-10 text-xs text-muted-foreground">
            <span>← Older wins</span>
            <span>Now →</span>
          </div>
        )}

        {/* Legend */}
        {!compact && (
          <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: tierColor || "hsl(var(--primary))" }}
              />
              <span>Below avg</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-orange-500" />
              <span>Above avg</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-muted border-2 border-white/50 animate-pulse" />
              <span>Current</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
